import { useState, useEffect, useRef } from "react";
import { ShoppingBag } from "lucide-react";

interface SplashScreenProps {
  onComplete: () => void;
}

export function SplashScreen({ onComplete }: SplashScreenProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<"enter" | "loading" | "exit">("enter");
  const splashRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 30);

    const phaseTimer = setTimeout(() => setPhase("loading"), 100);
    const exitTimer = setTimeout(() => setPhase("exit"), 2500);
    const completeTimer = setTimeout(() => {
      setIsVisible(false);
      onComplete();
    }, 3200);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(phaseTimer);
      clearTimeout(exitTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  useEffect(() => {
    if (!isVisible) return;

    const prevOverflow = document.body.style.overflow;
    const prevUserSelect = document.body.style.userSelect;

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }

    document.body.style.overflow = "hidden";
    document.body.style.userSelect = "none";

    splashRef.current?.focus();

    const blockEvent = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      if ("stopImmediatePropagation" in event) {
        (event as Event & { stopImmediatePropagation: () => void }).stopImmediatePropagation();
      }
    };

    const blockedEvents = [
      "click",
      "dblclick",
      "mousedown",
      "mouseup",
      "mousemove",
      "pointerdown",
      "pointerup",
      "pointermove",
      "touchstart",
      "touchmove",
      "touchend",
      "wheel",
      "contextmenu",
      "keydown",
      "keyup",
      "keypress",
      "submit",
    ] as const;

    blockedEvents.forEach((eventName) => {
      window.addEventListener(eventName, blockEvent, { capture: true, passive: false });
    });

    const trapFocus = (event: FocusEvent) => {
      const splashNode = splashRef.current;
      const target = event.target as Node | null;
      if (!splashNode || !target) return;

      if (!splashNode.contains(target)) {
        event.stopPropagation();
        if (event.target instanceof HTMLElement) {
          event.target.blur();
        }
        splashNode.focus();
      }
    };

    document.addEventListener("focusin", trapFocus, true);

    return () => {
      blockedEvents.forEach((eventName) => {
        window.removeEventListener(eventName, blockEvent, true);
      });
      document.removeEventListener("focusin", trapFocus, true);
      document.body.style.overflow = prevOverflow;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div
      ref={splashRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      className={`
        fixed inset-0 z-9999 overflow-hidden
        bg-neutral-950
        flex items-center justify-center
        transition-all duration-700 ease-in-out
        ${phase === "exit" ? "opacity-0 scale-105" : "opacity-100 scale-100"}
      `}
    >
      {/* Center radial accent glow */}
      <div
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-1000 ${phase === "loading" ? "opacity-100" : "opacity-0"}`}
      >
        <div
          className="w-175 h-175 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(201,169,110,0.07) 0%, transparent 65%)",
          }}
        />
      </div>

      {/* Corner ambient blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />

      {/* Horizontal accent lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/8 left-0 right-0 h-px bg-linear-to-r from-transparent via-accent/15 to-transparent" />
        <div className="absolute bottom-1/8 left-0 right-0 h-px bg-linear-to-r from-transparent via-accent/15 to-transparent" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center space-y-10 px-6">

        {/* Logo Container */}
        <div
          className={`
            relative transition-all duration-700 ease-out
            ${phase === "loading" ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-90 translate-y-6"}
          `}
        >
          {/* Spinning aurora border */}
          {/* <div
            className="absolute -inset-[2px] rounded-[26px] animate-spin"
            style={{
              background:
                "conic-gradient(from 0deg, transparent 0deg, rgba(201,169,110,0.9) 60deg, transparent 120deg)",
              animationDuration: "3s",
            }}
          /> */}

          {/* Logo Card */}
          <div className="relative p-10 rounded-3xl bg-white/80 backdrop-blur-sm border border-white/10 shadow-glow">
            <div className="absolute inset-0 bg-linear-to-br from-accent/8 via-transparent to-transparent rounded-3xl pointer-events-none" />

            <div className="relative">
              <img
                src="/assets/Images/logo/logo.webp"
                alt="My Perfume"
                className="w-36 h-36 mx-auto object-contain"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = "none";
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = "flex";
                }}
              />
              {/* Fallback */}
              <div
                className="w-36 h-36 mx-auto bg-neutral-800 rounded-2xl items-center justify-center"
                style={{ display: "none" }}
              >
                <ShoppingBag className="w-16 h-16 text-accent" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* Brand Name */}
        <div
          className={`
            text-center space-y-5 transition-all duration-700 delay-150
            ${phase === "loading" ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}
          `}
        >
          <h1
            className="text-5xl md:text-6xl font-black text-white/80 tracking-[0.15em]"
            style={{ textShadow: "0 0 40px rgba(201,169,110,0.35)" }}
          >
            My Perfume
          </h1>

          {/* Animated width divider */}
          <div
            className={`h-px mx-auto bg-linear-to-r from-transparent via-accent to-transparent transition-all duration-1000 delay-300 ${phase === "loading" ? "w-28" : "w-0"}`}
          />

          <p className="text-neutral-500 text-xs font-medium tracking-[0.3em] uppercase">
            Premium Shopping Experience
          </p>
        </div>

        {/* Progress Bar */}
        <div
          className={`
            w-72 max-w-full transition-all duration-700 delay-300
            ${phase === "loading" ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}
          `}
        >
          <div className="relative h-0.5 bg-neutral-800 rounded-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-200 ease-out"
              style={{
                width: `${progress}%`,
                background: "linear-gradient(90deg, rgba(201,169,110,0.5), #c9a96e)",
                boxShadow:
                  "0 0 10px rgba(201,169,110,0.7), 0 0 20px rgba(201,169,110,0.3)",
              }}
            />
          </div>
          <div className="flex items-center justify-end mt-2">
            <span className="text-accent/50 font-mono text-[10px] tracking-widest">
              {progress}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SplashScreen;
